/**
 * Quick PDF export from the export dialog: a template, the floors and a
 * scale; every plan viewport is framed on its floor automatically. (Sheets
 * give full control over framing, fields and revisions.)
 */

import { useCallback, useContext, useMemo, useState } from 'react';
import { usePrintLengthUnit } from '../usePrintLengthUnit';
import { useTemplateLibraryEvents } from '../../templateLibrary/useTemplateLibraryEvents';
import { useParams } from 'react-router-dom';
import { getFloorPlanePositionProperty, hasFloorPlanePosition } from '@pointorama/potree-shared';
import {
  BUILTIN_PRINT_TEMPLATES,
  SHEET_DOC_SCHEMA_VERSION,
  defaultViewportSettings,
  expandPages,
  fitStandardScale,
  pageViewports,
  pageInstanceKey,
  parsePrintTemplateSpec,
  viewportSettingsKey,
  type PlanTextCategory,
  type PrintFloorInfo,
  type PrintTemplateSpec,
  type SheetDoc,
  type TextSize,
  type ViewportSettings,
} from 'shared-types';
import { UserContext } from '../../../contexts/UserContext';
import { useProject } from '../../../hooks/potree/useProject';
import { useProjectTemplateLibraryQuery, type ProjectTemplateLibraryQuery } from '../../../types/graphqlTypes';
import { orgPrintFontsOf } from '../fonts/orgFonts';
import { usePlanContent, type BuildPlanEntities } from '../content/usePlanContent';
import type { BrandDetailsResult } from '../../templateLibrary/BrandDetailsDialog';
import { loadPrintImages } from './printImages';
import { useBuiltinTemplateText } from '../builtinTemplateText';

/** A template the project can print with. */
export interface PrintTemplateChoice {
  source: 'builtin' | 'org';
  id: string;
  version: number;
  name: string;
  description: string;
  spec: PrintTemplateSpec;
  assets: { id: string; url: string }[];
}

type TemplateLibrary = ProjectTemplateLibraryQuery['projectTemplateLibrary'];

export function templateChoices(library: TemplateLibrary | undefined): PrintTemplateChoice[] {
  const hidden = new Set(library?.config.hiddenBuiltinIds ?? []);
  const builtins = BUILTIN_PRINT_TEMPLATES.filter((t) => !hidden.has(t.id)).map((t): PrintTemplateChoice => ({ source: 'builtin', id: t.id, version: t.version, name: t.name, description: t.description, spec: t.spec, assets: [] }));
  const orgs = (library?.templates ?? []).flatMap((t): PrintTemplateChoice[] => {
    const parsed = parsePrintTemplateSpec(t.publishedSpec);
    if (!parsed.ok) return [];
    return [{ source: 'org', id: t.id, version: t.version, name: t.name, description: t.description ?? '', spec: parsed.spec, assets: t.assets.map((a) => ({ id: a.id, url: a.url })) }];
  });
  return [...orgs, ...builtins];
}

/** The project's template library (brand kit + published templates) and the template list. */
export function usePrintTemplates() {
  const { projectId = '' } = useParams();
  const query = useProjectTemplateLibraryQuery({ variables: { projectId }, skip: !projectId, fetchPolicy: 'cache-and-network' });
  const library = query.data?.projectTemplateLibrary;
  // Brand kit and published templates change elsewhere: follow them.
  useTemplateLibraryEvents({ organisationId: library?.organisationId, onChanged: () => void query.refetch() });
  const builtinText = useBuiltinTemplateText();
  // Built-in names and descriptions in the user's language (org templates are named by the org).
  const choices = useMemo(() => templateChoices(library).map((c) => (c.source === 'builtin' ? { ...c, ...builtinText(c) } : c)), [library, builtinText]);
  const defaultChoice = useMemo(() => {
    const ref = library?.config.defaultTemplate;
    const preferred = ref ? choices.find((c) => c.id === ref.id && c.source === (ref.source === 'ORG' ? 'org' : 'builtin')) : undefined;
    return preferred ?? choices.find((c) => c.source === 'builtin' && c.id === 'a3-plan') ?? choices[0] ?? null;
  }, [library, choices]);
  return { library, choices, defaultChoice, loading: query.loading };
}

export interface QuickPdfOptions {
  template: PrintTemplateChoice;
  floors: readonly { id: string; name: string }[];
  /** A scale denominator, or 'fit': the most detailed standard scale showing the whole floor. */
  scale: number | 'fit';
  textSizes: Partial<Record<PlanTextCategory, TextSize>>;
  /** Set by reframing in the export dialog: overrides the per-floor centering and the scale. */
  framing?: QuickFraming | null;
  /** Brand details just filled in before this export (the library may not have refetched yet). */
  brandDetails?: BrandDetailsResult;
}

/** Where the quick export's plan views look: center, rotation (CCW from world +Y up the page) and scale. */
export interface QuickFraming {
  center: { x: number; y: number };
  headingDeg: number;
  scaleDen: number;
}

function floorElevation(floorId: string): number {
  try {
    if (!hasFloorPlanePosition(floorId)) return 0;
    const z = getFloorPlanePositionProperty(floorId).value?.z;
    return typeof z === 'number' ? z : 0;
  } catch {
    return 0;
  }
}

/** Margin around a floor when fitting the scale (share of the viewport). */
const FIT_MARGIN = 0.9;

/**
 * Settings of every plan viewport of a quick export: centered on its floor's
 * plan at the chosen (or fitting) scale, or — once reframed — at the
 * framing's center, rotation and scale (one framing for every floor).
 */
export function quickViewports(
  spec: PrintTemplateSpec,
  sheet: SheetDoc,
  floors: readonly PrintFloorInfo[],
  options: Pick<QuickPdfOptions, 'scale' | 'textSizes' | 'framing'>,
  planEntities: BuildPlanEntities,
): Record<string, ViewportSettings> {
  const viewports: Record<string, ViewportSettings> = {};
  const capturedAt = new Date().toISOString();
  for (const instance of expandPages(spec, sheet, floors)) {
    const floor = instance.floor ?? floors[0];
    if (!floor) continue;
    for (const element of pageViewports(instance.page)) {
      if (!element.allowedKinds.includes('PLAN_VECTOR')) continue;
      const probe = { ...defaultViewportSettings('PLAN_VECTOR', element.defaultScaleDen), textSizes: options.textSizes };
      const key = viewportSettingsKey(pageInstanceKey(instance.page.id, instance.floor?.id ?? null), element.id);
      const base = { ...probe, floorId: floor.id, followPageFloor: instance.floor !== null, capturedAt };
      if (options.framing) {
        viewports[key] = { ...base, center: options.framing.center, headingDeg: options.framing.headingDeg, scaleDen: options.framing.scaleDen };
        continue;
      }
      const { bounds } = planEntities(floor.id, probe, 0.7);
      if (!bounds) continue;
      const widthM = bounds.maxX - bounds.minX;
      const heightM = bounds.maxY - bounds.minY;
      const scaleDen = options.scale === 'fit' ? fitStandardScale(widthM, heightM, element.wMm * FIT_MARGIN, element.hMm * FIT_MARGIN) : options.scale;
      viewports[key] = { ...base, scaleDen, center: { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 } };
    }
  }
  return viewports;
}

export function useQuickPdfExport() {
  const { library } = usePrintTemplates();
  const planEntities = usePlanContent();
  const currentUser = useContext(UserContext);
  const lengthUnit = usePrintLengthUnit();
  const projectQuery = useProject();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (options: QuickPdfOptions): Promise<SheetDoc | null> => {
      setBusy(true);
      setError(null);
      try {
        const { template } = options;
        const floors: PrintFloorInfo[] = options.floors.map((f) => ({ id: f.id, name: f.name, elevation: floorElevation(f.id) }));
        if (floors.length === 0) return null;
        const config = library?.config;
        // pdf-lib + fontkit (~1 MB) load on the first export, not with the app.
        const [{ createPrintFontRegistry }, { downloadPdf, exportPdf }] = await Promise.all([import('../fonts/printFonts'), import('./exportPdf')]);
        const fonts = await createPrintFontRegistry(orgPrintFontsOf(config?.fonts));

        // Frame every plan viewport on its floor: centered on the floor's extent, at the chosen (or fitting) scale.
        const baseSheet: SheetDoc = {
          schemaVersion: SHEET_DOC_SCHEMA_VERSION,
          templateRef: { source: template.source, id: template.id, version: template.version },
          fieldValues: {},
          revisions: [],
          floorIds: floors.map((f) => f.id),
          viewports: {},
          hiddenPages: [],
        };
        const viewports = quickViewports(template.spec, baseSheet, floors, options, planEntities);

        const images = await loadPrintImages({ logoUrl: options.brandDetails?.logoUrl ?? config?.logo?.url ?? null, assets: template.assets });
        try {
          const projectName = projectQuery.data?.projectById?.name ?? '';
          const organisation = currentUser.organisations?.find((o) => o.id === library?.organisationId);
          const brand = { ...config?.brand, ...options.brandDetails?.brand };
          const bytes = await exportPdf({
            spec: template.spec,
            sheet: { ...baseSheet, viewports },
            floors,
            sources: {
              project: { name: projectName },
              org: { name: organisation?.name ?? '', address: brand?.address ?? undefined, phone: brand?.phone ?? undefined, email: brand?.email ?? undefined, website: brand?.website ?? undefined, vat: brand?.vat ?? undefined },
              sheet: { name: template.name },
              user: { name: `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim() },
              units: lengthUnit,
            },
            fonts,
            images,
            planEntities,
            title: `${projectName} — ${template.name}`,
          });
          downloadPdf(bytes, `${projectName || 'plan'} - ${template.name}`);
        } finally {
          images.dispose();
        }
        // What was printed, as a sheet document (the export dialog keeps it as the project's first sheet).
        return { ...baseSheet, viewports };
      } catch (e) {
        console.error('PDF export failed', e);
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [library, planEntities, currentUser, projectQuery.data, lengthUnit],
  );

  return { exportPdf: run, busy, error };
}
